
import { LiveSiteEventSeverity, LiveSiteEventState } from '../models/status';
import { isUndefinedOrWhiteSpace } from './strings';

/**
 * The service state when taking state and severity values into consideration
 */
export enum NormalizedServiceState {
    Healthy,
    Advisory,
    Degraded,
    Unhealthy,
    NotApplicable,
    Unrecognized
}

/**
 * Get a normalized service state which takes into account state and severity to get the be-all end-all authority on the service state.
 * @param state
 * @param severity
 */
export const getNormalizedServiceState = (state: LiveSiteEventState | undefined, severity: LiveSiteEventSeverity | undefined): NormalizedServiceState => {
    if (state === 'Resolved') {
        return NormalizedServiceState.Healthy;
    }

    switch (severity) {
        case 'Advisory':
            return NormalizedServiceState.Advisory;
        case 'Degraded':
            return NormalizedServiceState.Degraded;
        case 'Unhealthy':
            return NormalizedServiceState.Unhealthy;
        default:
            // In case we run into an unknown severity give an unrecognized state
            // i.e. if a new severity level is added and the upstream does not filter this out
            return NormalizedServiceState.Unrecognized;
    }
};

/**
 * Get a consistent set of text to show for a severity level on a service.
 *
 * @param region The applicable region to show if this status is not available to show there
 * @param state The state of the event
 * @param severity The severity of the status
 */
export const getSeverityText = (region: string | undefined, state: LiveSiteEventState | undefined, severity: LiveSiteEventSeverity | undefined) => {
    switch (getNormalizedServiceState(state, severity)) {
        case NormalizedServiceState.Healthy:
            return 'Healthy';
        case NormalizedServiceState.Advisory:
            return 'Advisory';
        case NormalizedServiceState.Degraded:
            return 'Degraded';
        case NormalizedServiceState.Unhealthy:
            return 'Unhealthy';
        case NormalizedServiceState.NotApplicable:
            return !isUndefinedOrWhiteSpace(region) ? `Not available in ${region}` : 'Not applicable';
        default:
            return 'Not applicable';
    }
};
